import { useEffect, useMemo, useState } from "react";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { displayToast } from "../../components/app/AppToast";
import Loader from "../../components/Loader";
import NotificationItem from "../../components/NotificationItem";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import {
  apiClient,
  useApiQuery,
  getApiQueryData,
  setApiQueryBody,
} from "../../services/api";
import { gaEventNotificationCenterViewed } from "../../tools/analytics/domains/notification.analytics";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";
import { useMarkNotificationsAsRead } from "./useMarkNotificationsAsRead";

export default function NotificationCenter() {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  const personals = useApiQuery("/webapp/private/notification/personals", {});
  const broadcasts = useApiQuery("/webapp/private/notification/broadcasts", {});

  useMarkNotificationsAsRead(personals, broadcasts);

  const failedQuery =
    personals.state === "error"
      ? personals
      : broadcasts.state === "error"
        ? broadcasts
        : null;
  const isLoading =
    personals.state !== "success" || broadcasts.state !== "success";

  // Merge and sort by creationDate desc
  const allNotifications = useMemo(() => {
    if (personals.state !== "success" || broadcasts.state !== "success")
      return [];
    return [...personals.body, ...broadcasts.body].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [personals, broadcasts]);

  useEffect(() => {
    if (currentUser) gaEventNotificationCenterViewed(currentUser.uid);
  }, [currentUser]);

  async function handleSoftDeleteNotification(notificationId: string) {
    if (removingIds.has(notificationId)) return;

    const notification = allNotifications.find((n) => n.id === notificationId);
    if (!notification) return;

    // Start animation
    setRemovingIds((prev) => new Set(prev).add(notificationId));

    // Wait for animation
    await new Promise((r) => setTimeout(r, 300));

    const isBroadcast = notification.user === null;

    // Snapshot body for rollback
    const previousBroadcasts = isBroadcast
      ? getApiQueryData("/webapp/private/notification/broadcasts")
      : null;
    const previousPersonals = isBroadcast
      ? null
      : getApiQueryData("/webapp/private/notification/personals");

    // Optimistically remove from cache
    if (isBroadcast) {
      setApiQueryBody(
        "/webapp/private/notification/broadcasts",
        undefined,
        (body) => body.filter((n) => n.id !== notificationId)
      );
    } else {
      setApiQueryBody(
        "/webapp/private/notification/personals",
        undefined,
        (body) => body.filter((n) => n.id !== notificationId)
      );
    }

    setRemovingIds((prev) => {
      const next = new Set(prev);
      next.delete(notificationId);
      return next;
    });

    try {
      if (isBroadcast) {
        await apiClient.delete("/webapp/private/notification/broadcasts/:id", {
          param: { id: notificationId },
        });
      } else {
        await apiClient.delete("/webapp/private/notification/personals/:id", {
          param: { id: notificationId },
        });
      }
    } catch {
      if (isBroadcast && previousBroadcasts !== null) {
        setApiQueryBody(
          "/webapp/private/notification/broadcasts",
          undefined,
          () => previousBroadcasts
        );
      }
      if (!isBroadcast && previousPersonals !== null) {
        setApiQueryBody(
          "/webapp/private/notification/personals",
          undefined,
          () => previousPersonals
        );
      }
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <ContentHeader
          transparent
          title={t("Notifications")}
          backLabel={t("Profile")}
        />
      }
    >
      <div className="mx-[22px] my-[16px] text-[21px] text-primary-black font-black leading-[120%]">
        {t("Notification Center")}
      </div>
      <hr />
      {failedQuery !== null ? (
        <p className="px-5 py-4 text-ui-body text-ds-slate-600">
          {failedQuery.message}
        </p>
      ) : isLoading ? (
        <Loader expanded />
      ) : (
        <div>
          {allNotifications.length === 0 ? (
            <div className="p-[16px_22px]  font-light text-[14px] text-secondary-bluegrey-6 leading-[130%]">
              {t("You have no new notifications.")}
            </div>
          ) : (
            <div className="px-[22px]">
              {allNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onDelete={() => handleSoftDeleteNotification(notification.id)}
                  isRemoving={removingIds.has(notification.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </ScrollableLayout>
  );
}
